import { useScrollLock } from "@/hooks/useScrollLock";
import { useTranslation } from "@repo/shared/i18n";
import { useEffect } from "react";

import Button from "./Button";
import { CloseIcon } from "./features/SquadBuilder/RightPanel/PanelHeader/PanelHeader";

interface PopupProps {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
  titleIcon?: React.ReactNode;
  absolute?: boolean;
  actionTitle?: string;
  handleActionClicked?: () => void;
}

export const Popup = ({
  title,
  titleIcon,
  children,
  onClose,
  absolute = false,
  actionTitle,
  handleActionClicked,
}: PopupProps) => {
  const { t } = useTranslation();
  // Lock scroll when not absolute positioned
  useScrollLock(!absolute);

  useEffect(() => {
    if (absolute) {
      scrollTo(0, 0);
    }
  }, [absolute]);

  const Children = () => (
    <div
      onClick={(event) => event.stopPropagation()}
      className="bg-panel-background flex flex-col w-fit mx-auto items-center mobile:w-auto rounded-2xl"
    >
      <header className="h-16 w-full flex items-center justify-between bg-panel-base rounded-t-2xl">
        <div className="w-12" />
        <h2 className="flex flex-row items-center gap-x-2">
          {title}
          {titleIcon}
        </h2>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="p-4"
        >
          <CloseIcon />
        </button>
      </header>
      <main>{children}</main>
      <footer className="h-24 flex items-center rounded-b-2xl">
        <Button
          variant="primary"
          label={actionTitle || t("done")}
          onClick={(e) => {
            e.stopPropagation();
            if (handleActionClicked) {
              handleActionClicked();
            }
            onClose();
          }}
        />
      </footer>
    </div>
  );

  return absolute ? (
    <>
      <div className="top-0 -translate-y-60 left-0 right-0 mobile:right-auto mobile:left-1/2 mobile:-translate-x-1/2 z-50 absolute">
        <Children />
      </div>
      <div
        className="fixed top-0 left-0 w-full h-full bg-black/60 z-40"
        onClick={onClose}
      />
    </>
  ) : (
    <div
      className="fixed top-0 left-0 right-0 w-full h-full bg-black/60 z-50"
      onClick={onClose}
    >
      <div className="top-2/5 -translate-y-1/2 absolute mobile:left-1/2 mobile:-translate-x-1/2">
        <Children />
      </div>
    </div>
  );
};
